import { Input, SimpleGrid } from '@chakra-ui/react'

import { FormField } from '../../../components/FormField'
import { FormSelect, ISelectOption, SearchableSelect } from '../../../components/Select'
import { TEmployeeStatus } from '../../../services/Employee/types'
import { JobPosition } from '../../../services/JobPosition/types'
import { TFormErrors } from '../Admission/admissionForm'
import { IEmployeeForm } from './employeeForm'

interface IEmployeeProfessionalFieldsProps {
  form: IEmployeeForm
  update: (patch: Partial<IEmployeeForm>) => void
  errors: TFormErrors
  isDisabled: boolean
  /** The collaborator's status; a new one is registered straight as active. */
  status: TEmployeeStatus
  roles: JobPosition[]
  sectors: ISelectOption<number>[]
  /** Offered from the full name; `null` while loading. */
  usernames: string[] | null
  /** The role saved now, offered even if it was deactivated since. */
  currentRoleId?: number | null
}

/**
 * Role, sector, username and the dates RH sets. Choosing a role brings its sector, which RH can
 * still change; the username comes from the name, with the admission form's rules.
 */
export default function EmployeeProfessionalFields({
  form,
  update,
  errors,
  isDisabled,
  status,
  roles,
  sectors,
  usernames,
  currentRoleId
}: IEmployeeProfessionalFieldsProps) {
  const roleOptions = roles
    .filter((role) => (role.status === 'active' && role.is_complete) || role.id === currentRoleId)
    .map((role) => ({ value: role.id, label: role.status === 'active' ? role.name : `${role.name} (desativado)` }))

  // The saved username stays offered while the list loads or after the name moved on.
  const usernameOptions =
    (usernames ?? []).includes(form.username) || !form.username
      ? (usernames ?? [])
      : [...(usernames ?? []), form.username]

  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
      <FormField label="Cargo" error={errors.job_position_id} isRequired>
        <SearchableSelect<number>
          options={roleOptions}
          value={form.job_position_id ?? ''}
          onChange={(roleId) => {
            const role = roles.find((item) => item.id === roleId)
            update({ job_position_id: roleId === '' ? null : roleId, sector_id: role?.sector_id ?? form.sector_id })
          }}
          entity="cargo"
          isDisabled={isDisabled}
        />
      </FormField>
      <FormField
        label="Setor"
        error={errors.sector_id}
        helperText="Vem do cargo; troque só se a pessoa estiver em outro setor."
      >
        <FormSelect
          value={form.sector_id ?? ''}
          onChange={(sectorId) => update({ sector_id: sectorId ? Number(sectorId) : null })}
          options={sectors}
          isDisabled={isDisabled}
        />
      </FormField>
      <FormField
        label="Nome de usuário"
        error={errors.username}
        helperText={
          usernames && usernames.length === 0 && !form.username
            ? 'Informe o nome completo, com sobrenome, para ver as opções.'
            : 'Gerado a partir do nome completo.'
        }
      >
        <FormSelect
          value={form.username}
          onChange={(username) => update({ username })}
          options={usernameOptions.map((username) => ({ value: username, label: username }))}
          isLoading={usernames === null}
          isDisabled={isDisabled}
        />
      </FormField>
      {status !== 'pending' && (
        <FormField label="Data de Admissão" error={errors.admission_date} isRequired>
          <Input
            type="date"
            value={form.admission_date}
            onChange={(e) => update({ admission_date: e.target.value })}
            isDisabled={isDisabled}
          />
        </FormField>
      )}
      {status === 'inactive' && (
        <FormField label="Data de Desligamento" error={errors.termination_date} isRequired>
          <Input
            type="date"
            value={form.termination_date}
            min={form.admission_date || undefined}
            onChange={(e) => update({ termination_date: e.target.value })}
            isDisabled={isDisabled}
          />
        </FormField>
      )}
    </SimpleGrid>
  )
}
